import {
  CheckOutlined,
  CloseOutlined,
  CompressOutlined,
  ExpandOutlined,
  FilterOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { Button, Checkbox, Menu, Modal, Popover } from "antd";
import { intersection } from "lodash";
import { useEffect, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useSelector } from "react-redux";
import AuthZButton from "src/components/AuthzButton";
import AuthZGuidedTour from "src/components/authz-tour-guide";
import DecisionAndReasoningComponentWithRejectedNotification
  from "src/components/review-decision-and-reasoning-with-error-identfication";
import { TicketComponent } from "src/components/ticketComponent";
import UserForReviewTask from "src/components/user-for-review-task";
import { IsFeatureEnabledOrNot } from "../../../../../../FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "../../../../../../FeatureToggles/toggleList";
import { isNullOrEmptyString, isNullOrUndefined, renderWithStyle } from "../../../../../../common/utils";
import { formatUtcDateToLocal, parseDate } from "../../../../../../common/utils/TimeUtils";
import DownloadReport from "../../../../../../components/DownloadReport";
import { WithRoleLinkToolTip } from "../../../../../../components/RoleLinkToolTip";
import { AuthZTable, defaultPageSize, defaultPageSizeOptions } from "../../../../../../components/authZTable";
import { BatchEditComponent } from "../../../../../../components/batchEditComponent";
import { ColumnRepresentations } from "../../../../../../components/column-representations/model/ColumnRepresentations";
import { ReviewTaskAction, sendReviewTaskEvent } from "../../../../../../components/matomoClient";
import { PreviewModalComponent } from "../../../../../../components/previewModalComponent";
import { ReassignComponent } from "../../../../../../components/reassignComponent";
import { SubmitBoard } from "../../../../../../components/submitBoard";
import { TextWithSubTitle } from "../../../../../../components/textWithSubTitle";
import { getSelectedColumns, getTotalColumns } from "../../../model";
import { FilterKeys, ReviewTasksController } from "./controller";
import { previewAndSubmitColumnGenerator } from "./dataAndColumnGenerator";
import {
  DecisionAndReasoningFilterDropdown,
  filterDecisionAndReasoningColumn,
} from "./decision-and-reasoning-filter-dropdown";
import "./index.scss";
import { ReviewTasks as ReviewTasksModel } from "./model/ReviewTasks";
import { addCustomizedSorterLogicToColumns, addSortWarningToColumns } from "./review-tasks-column-helper";

function FilterMenu({ filters, showMyListFilter, setFilterConfigurations, reviewStatus }) {
  function RenderOptions({ label, isChecked }) {
    return (
      <div>
        <Checkbox checked={isChecked}>{label}</Checkbox>
      </div>
    );
  }

  const myListFilter = showMyListFilter
    ? [
      {
        key: FilterKeys.ShowMyTasks,
        label: <RenderOptions label="My tasks" isChecked={filters[FilterKeys.ShowMyTasks]} />,
      },
    ]
    : [];

  let menuProps = [
    ...myListFilter,
    {
      key: FilterKeys.ShowSavedDecision,
      label: (
        <RenderOptions
          label="Saved review decisions"
          isChecked={filters[FilterKeys.ShowSavedDecision]}
        />
      ),
    },
    {
      key: FilterKeys.ShowPendingDecisions,
      label: (
        <RenderOptions
          label="Pending review decisions"
          isChecked={filters[FilterKeys.ShowPendingDecisions]}
        />
      ),
    },
    {
      key: FilterKeys.ShowSubmittedDecisions,
      label: (
        <RenderOptions
          label="Submitted review decisions"
          isChecked={filters[FilterKeys.ShowSubmittedDecisions]}
        />
      ),
    },
  ];

  const selectedFilters = [];
  for (const key in filters) {
    if (filters[key] && key !== FilterKeys.ShowAll) {
      selectedFilters.push(key);
    }
  }

  const isSaveAsDraftFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.SaveAsDraft);
  if (!isSaveAsDraftFeatureEnabled || !reviewStatus) {
    menuProps = menuProps.filter((prop) => prop.key !== FilterKeys.ShowSavedDecision);
  }

  return (
    <div className="filter-dropdown">
      <b>Display</b>
      <Menu
        items={menuProps}
        multiple={true}
        onSelect={({ selectedKeys }) => {
          setFilterConfigurations(selectedKeys);
        }}
        onDeselect={({ selectedKeys }) => {
          setFilterConfigurations(selectedKeys);
        }}
        selectedKeys={selectedFilters}
      />
      <center>
        <span
          className="clear-filters"
          onClick={() => {
            setFilterConfigurations([]);
          }}
        >
          Clear filters
        </span>
      </center>
    </div>
  );
}

function renderDecisionsMadeByUsers(taskCount, title) {
  const decisionCount = (text, count) => (
    <div className={"decisions-count-item"}>
      <div className={"decisions-count-text"}>{text}</div>
      <div className="decisions-count">{count}</div>
    </div>
  );

  return (
    <div className="decisions-split-up">
      <div style={{ fontWeight: 600, color: "#000000" }}>
        {title} &nbsp;&nbsp; {taskCount.total}
      </div>
      {taskCount.total > 0 &&
        <div style={{ color: "#333333", padding: "0.5rem 0" }}>
          {decisionCount("No change needed", taskCount.noChangeNeeded)}
          {decisionCount("Needs adjustment", taskCount.needsAdjustment)}
          {decisionCount("Needs removal", taskCount.needsRemoval)}
        </div>
      }
    </div>
  );
}

export const ReviewTasks = ({
                              appId,
                              reviewId,
                              status,
                              pageSize,
                              setTabPageSize,
                              reviewName,
                              activeTabId,
                              accessTypeLink,
                              appHeader,
                            }) => {
  const isReviewInProgress = status === "In Progress";
  const fullScreenHandle = useFullScreenHandle();

  const {
    columns,
    reviewTasks,
    previewReviewTasks,
    isPreviewTableLoading,
    isLoading,
    upsertEditedReviewTasks,
    onSubmitBtnClick,
    rejectedReviewTasks,
    showTaskRejectedReason,
    rowSelection,
    selectedRowKeys,
    upsertBatchSelectedReview,
    removeBatchSelectedReview,
    batchSelectAction,
    batchSelectComment,
    batchSelectDecisionReason,
    batchSelectCommentRequired,
    batchActionRequiredWhenCommentIsFilled,
    onBatchSelectedSubmit,
    reassignReviewTask,
    resetTask,
    removeEditedReviewTasks,
    setAutomaticallyRemovedColumns,
    onSaveButtonClick,
    handleClearSaved,
    previewTableRowClassName,
    submitButtonHandler,
    isSaveAsDraftFeatureEnabled,
    previewTasksDatasource,
    userDetails,
    setPreviewReviewTasks,
    setPreviewModalVisible,
    isPreviewModalVisible,
    ignoredReviewTasks,
    setIgnoredReviewTasks,
    countOfTasksAddedByOtherAccessOwners,
    countOfTasksAddedByCurrentUser,
    previewModalCancelHandler,
    setFilterConfigurations,
    filters,
    isSubmitButtonDisabled,
    noOfIgnoredRows,
    setNoOfIgnoredRows,
    showMyListFilter,
    showExportMyTasksButton,
    onSelectUserGroupCheckBox,
    onUnSelectUserGroupCheckBox,
    isUserGroupCheckboxDisabled,
    hoveredTaskIds,
    setHoveredTaskIds,
    reviewInFullScreenFeatureEnabled,
    tourState,
    reassignReviewTasks,
    setTourState,
    isAoOrGaa
  } = ReviewTasksController(appId, reviewId, activeTabId, isReviewInProgress);

  const reasons = useSelector((rootState) => rootState.appReviewTasks.reasons);
  const viewableReasons = reviewTasks.viewableDecisionReasons(filters);
  const previewTasksReasons = previewTasksDatasource
    .filter((task) => !isNullOrUndefined(task.decisionReason))
    .map((task) => task.decisionReason.id);
  const [isUserGroupingEnabled, setIsUserGroupingEnabled] = useState(true);
  const isGroupDelegationEnabled = IsFeatureEnabledOrNot(ToggleList.BatchDelegation);

  const reasonsAvailableForFilter = reasons.filter((reason) => viewableReasons.includes(reason.id));
  const previewTasksReasonsAvailableForFilter = reasons.filter((reason) =>
    previewTasksReasons.includes(reason.id),
  );
  const [currentReviewTasksInTable, setCurrentReviewTasksInTable] = useState(
    new ReviewTasksModel([]),
  );
  const popupContainer = (() => document.querySelector(`.ReviewTaskContainer-${reviewId}`));

  function appliedFiltersCount() {
    if (filters[FilterKeys.ShowAll]) {
      return 0;
    }
    let count = 0;
    for (const key in filters) {
      if (filters[key]) {
        count += 1;
      }
    }
    return count;
  }

  function isFilterApplied() {
    return appliedFiltersCount() > 0;
  }

  function calculatedDisabled(record) {
    return (
      !(isReviewInProgress && record.hasEditAccess) ||
      isNullOrEmptyString(record.decision) ||
      record.decision === "Cleared" ||
      selectedRowKeys.length > 0
    );
  }

  function isReviewTaskUnassigned(record) {
    return record.reassignment.isReAssignable && record.reassignment.hasNoDelegations;
  }

  let modifiedColumns = columns.map((column, columnIndex) => {
    let { render, tableFilter, ...restKeys } = column;
    if (render === "TextWithSubTitleComponent") {
      render = (titles, record) => (
        <WithRoleLinkToolTip
          accessTypeLink={accessTypeLink}
          column={column}
          dataTestId={`role-link-${record.key}`}
          getPopupContainer={popupContainer}
        >
          <TextWithSubTitle titles={titles} />
        </WithRoleLinkToolTip>
      );
      restKeys = {
        ...restKeys,
        width: 160,
        render,
      };
    }
    if (!render) {
      restKeys.render = (text, record) => text
        ? (
          <WithRoleLinkToolTip
            accessTypeLink={accessTypeLink}
            column={column}
            dataTestId={`role-link-${record.key}`}
            getPopupContainer={popupContainer}
          >
            {text}
          </WithRoleLinkToolTip>
        )
        : null;
    }
    if (column.key === "reviewedAt") {
      render = (dateStr) => {
        if (dateStr === "") return dateStr;
        return formatUtcDateToLocal(parseDate(dateStr), "YYYY-MM-DD HH:mm:ss");
      };
      restKeys = {
        ...restKeys,
        sortEnabled: true,
        render,
      };
    }
    if (columnIndex === 0) {
      const renderWarningIfRejected = (userDetails, record) => {
        const className = rowClassName(record);
        if (typeof render == "function") {
          if (isUserGroupingEnabled) {
            const reviewTasksGroup = currentReviewTasksInTable.reviewTasksGroup(record.reviewTaskId);
            const reviewTaskIds = reviewTasksGroup.reviewTaskIds;
            const totalTasksForReviewee = reviewTasks.totalEditableTasksForReviewee(record.revieweeId);
            const shouldSelectUserTaskGroupCheckbox =
              totalTasksForReviewee.isUserTaskGroupCheckBoxSelected(selectedRowKeys);
            const totalEditableReviewTaskKeys = totalTasksForReviewee.keys();
            const selectedRowKeysForTheReviewee = intersection(selectedRowKeys, totalEditableReviewTaskKeys);
            const reAssignableTasks = currentReviewTasksInTable
              .findAllBy(reviewTaskIds)
              .filterReAssignableTasks();

            return renderWithStyle(
              { className, rowSpan: reviewTasksGroup.rowSpan },
              <UserForReviewTask
                onMouseEnter={() => setHoveredTaskIds(reviewTaskIds)}
                onMouseLeave={() => setHoveredTaskIds([])}
                userDetails={userDetails}
                record={record}
                className={shouldSelectUserTaskGroupCheckbox && "user-group-selected"}
                checkBox={reviewTaskIds.length > 1 && isAoOrGaa
                  ? (
                    <div className="user-tasks-group-actions">
                      <Checkbox
                        disabled={isUserGroupCheckboxDisabled(reviewTaskIds)}
                        checked={shouldSelectUserTaskGroupCheckbox}
                        indeterminate={
                          selectedRowKeysForTheReviewee.length > 0
                          && selectedRowKeysForTheReviewee.length < totalEditableReviewTaskKeys.length
                        }
                        onChange={(e) => {
                          return e.target.checked
                            ? onSelectUserGroupCheckBox(reviewTaskIds)
                            : onUnSelectUserGroupCheckBox(reviewTaskIds);
                        }}
                        style={{ padding: "0.5rem 0" }}
                        data-testid={`antd-check-${userDetails[1]}`}
                      />
                      {isGroupDelegationEnabled &&
                        <ReassignComponent
                          isEnable={reAssignableTasks.isReassignmentEnabled()}
                          isNotAssigned={reAssignableTasks.isNotDelegated()}
                          assigneeList={currentReviewTasksInTable.getAssignmentListForBulkDelegation(reviewTasksGroup.reviewTaskIds)}
                          reassignHandler={reassignReviewTasks}
                          taskId={reAssignableTasks.keys()}
                          reassignMessage={reAssignableTasks.tooltipMessage()}
                          getPopupContainer={popupContainer}
                          dataTestId={`batch-delegate-${userDetails[1]}`}
                        />
                      }
                    </div>
                  )
                  : null
                }
              />,
            );
          }
          return renderWithStyle(
            { className },
            <UserForReviewTask userDetails={userDetails} isGrouped={false} />,
          );
        }
        return renderWithStyle({ className }, userDetails);
      };
      restKeys = {
        ...restKeys,
        width: 180,
        fixed: "left",
        sortEnabled: true,
        defaultSortOrder: "ascend",
        render: renderWarningIfRejected,
      };
    } else if (render === "ReAssignComponent") {
      render = (text, record) => {
        const className = rowClassName(record);
        return renderWithStyle(
          { className },
          <ReassignComponent
            isEnable={record.reassignment.isReAssignable}
            isNotAssigned={record.reassignment.hasNoDelegations}
            assigneeList={record.reassignment.candidates}
            reassignHandler={reassignReviewTask}
            taskId={record.reviewTaskId}
            reassignMessage={record.reassignment.tooltipMessage}
            getPopupContainer={popupContainer}
          />,
        );
      };
      restKeys = {
        ...restKeys,
        width: 100,
        fixed: "right",
        searchEnabled: false,
        sortEnabled: false,
        singleSort: true,
        showSorterTooltip: {
          title: "Click to sort unassigned tasks",
        },
        sorter: (recordA, recordB) => {
          return isReviewTaskUnassigned(recordB) - isReviewTaskUnassigned(recordA);
        },
        render,
      };
    } else if (render === "TicketComponent") {
      render = (_, record) => {
        const className = rowClassName(record);
        return renderWithStyle(
          { className },
          <TicketComponent reviewTaskTicket={record.reviewTaskTicket} />,
        );
      };
      restKeys = {
        ...restKeys,
        key: "ticket",
        dataIndex: "ticket",
        width: 100,
        fixed: null,
        searchEnabled: false,
        sortEnabled: false,
        filterEnabled: false,
        className: `${restKeys.className}`,
        render,
      };
    } else if (render === "ReviewDecisionAndReasoningComponent") {
      render = (_text, record) => {
        const className = rowClassName(record);
        return renderWithStyle(
          { className },
          <DecisionAndReasoningComponentWithRejectedNotification
            record={record}
            reviewStatus={isReviewInProgress}
            selectedRowKeys={selectedRowKeys}
            resetTask={resetTask}
            removeEditedReviewTasks={removeEditedReviewTasks}
            upsertEditedReviewTasks={upsertEditedReviewTasks}
            userDetails={userDetails}
            handleClearSaved={handleClearSaved}
            calculatedDisabled={calculatedDisabled}
            rejectedTaskReason={
              showTaskRejectedReason
                ? rejectedReviewTasks?.find((rt) => rt.id === record.reviewTaskId)?.reason ?? null
                : null
            }
            getPopupContainer={popupContainer}
          />,
        );
      };
      restKeys = {
        ...restKeys,
        fixed: "right",
        className: `${restKeys.className} ant-column`,
        width: 240,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, visible }) => (
          <DecisionAndReasoningFilterDropdown
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
            confirm={confirm}
            decisionFilters={tableFilter}
            reasonsAvailableForFilter={reasonsAvailableForFilter}
            visible={visible}
            getPopupContainer={popupContainer}
          />
        ),
        onFilter: filterDecisionAndReasoningColumn,
        filters: [],
        filterEnabled: true,
        searchEnabled: false,
        sortEnabled: false,
        render,
      };
    } else {
      restKeys = {
        ...restKeys,
        sortEnabled: true,
        width: 180,
      };
    }
    return restKeys;
  });

  const selectedPaginatedColumns = useSelector((rootState) =>
    getSelectedColumns(rootState.appDetails, appId),
  );
  const totalColumns = useSelector((rootState) => getTotalColumns(rootState.appDetails, appId));
  modifiedColumns = modifiedColumns.filter(({ title }) => {
    return selectedPaginatedColumns.includes(title) || !totalColumns.includes(title);
  });

  const rowClassName = (record, index) => {
    const hoverClass = hoveredTaskIds.includes(record.reviewTaskId) ? "table-row-hover" : "";
    if (record.hasEditAccess && isReviewInProgress) {
      return `table-row-highlighted ${hoverClass}`;
    } else {
      return `table-row-disabled ${hoverClass}`;
    }
  };

  let titleProps = {};
  if (selectedRowKeys.length > 0) {
    titleProps = {
      title: () => (
        <BatchEditComponent
          id={reviewId}
          selectedCount={selectedRowKeys.length}
          onSubmit={() => {
            onBatchSelectedSubmit();
          }}
          defaultAction={batchSelectAction}
          defaultComment={batchSelectComment}
          defaultDecisionReason={batchSelectDecisionReason}
          handleActionChange={(action) =>
            upsertBatchSelectedReview({
              action,
              comment: null,
              decisionReason: null,
              reviewedBy: userDetails.fullName,
              reviewedAt: new Date().toISOString(),
            })
          }
          handleCommentChange={({ comment, decisionReason }) =>
            upsertBatchSelectedReview({
              comment: comment,
              decisionReason: decisionReason,
              reviewedBy: userDetails.fullName,
              reviewedAt: new Date().toISOString(),
            })
          }
          isCommentRequired={batchSelectCommentRequired}
          isActionRequired={batchActionRequiredWhenCommentIsFilled}
          getPopupContainer={popupContainer}
          onCancel={() => removeBatchSelectedReview()}
        />
      ),
    };
  }
  const [isSubmitConfirmationModalVisible, setIsSubmitConfirmationModalVisible] = useState(false);

  const isAllReviewTasksNotReAssign = reviewTasks.areAllTasksNotReAssignable();
  if (modifiedColumns?.length > 0 && isAllReviewTasksNotReAssign) {
    modifiedColumns = modifiedColumns?.filter((column) => column.title !== "Delegation");
  }

  modifiedColumns = addCustomizedSorterLogicToColumns(modifiedColumns);
  modifiedColumns = addSortWarningToColumns(modifiedColumns);

  function previewModalOkHandler() {
    onSubmitBtnClick();
    setIsSubmitConfirmationModalVisible(false);
    sendReviewTaskEvent(ReviewTaskAction.SubmitReviewTask, reviewName);
  }

  function setRemovedColumns() {
    setAutomaticallyRemovedColumns(
      columns
        .filter((column) => {
          return !modifiedColumns.some((modifiedColumn) => {
            return modifiedColumn.title === column.title;
          });
        })
        .map((column) => column.title),
    );
  }

  useEffect(setRemovedColumns, [JSON.stringify(modifiedColumns)]);

  const [isRemoveButtonEnabled, setIsRemoveButtonEnabled] = useState(false);

  const rowSelectionAttributes = {
    selectedRowKeys: ignoredReviewTasks,
    onChange: (selectedRowKeys) => {
      setNoOfIgnoredRows(selectedRowKeys.length);
      setIgnoredReviewTasks(selectedRowKeys);
    },
  };

  useEffect(() => {
    if (ignoredReviewTasks.length === 0) {
      setIsRemoveButtonEnabled(false);
    } else {
      setIsRemoveButtonEnabled(true);
    }
  }, [ignoredReviewTasks]);

  function closeModalIfAllDecisionsAreRemoved() {
    if (previewReviewTasks.submittable().length === 0) {
      setPreviewModalVisible(false);
    }
  }

  const previewModalHeader = (
    <>
      <div style={{ display: "flex", alignItems: "end", flexDirection: "column", padding: "10px" }}>
        <AuthZButton
          authZButtonType="primary"
          onClick={() => {
            setPreviewReviewTasks(previewReviewTasks.markTasksAsIgnored(ignoredReviewTasks));
            setNoOfIgnoredRows(0);
            setIsRemoveButtonEnabled(false);
            closeModalIfAllDecisionsAreRemoved();
          }}
          buttonDisabled={!isRemoveButtonEnabled}
        >
          Remove user from preview
        </AuthZButton>
        <div>
          <span style={{ fontSize: "12px" }}>
            <span style={{ color: "red", fontWeight: "bold" }}>* </span>Click to remove the selected
            users you do not wish to submit review decisions for
          </span>
        </div>
      </div>
    </>
  );

  const [previewModalFooter, setPreviewModalFooter] = useState(null);
  useEffect(() => {
    if (!isPreviewTableLoading) {
      setPreviewModalFooter(
        <>
          <div style={{ display: "flex", alignItems: "start", flexDirection: "column" }}>
            <div>
              <b>Are you sure you want to submit the changes?</b>
            </div>
            <div>
              <WarningFilled className={"removed-icon"} />
              <span style={{ fontSize: "12px" }}>
                Once Submitted, review actions can not be changed for this review.
              </span>
            </div>
            <div style={{ flexDirection: "row", paddingTop: "10px" }}>
              <AuthZButton
                authZButtonType="secondary"
                onClick={previewModalCancelHandler}
                icon={<CloseOutlined />}
              >
                Cancel
              </AuthZButton>
              <AuthZButton
                authZButtonType="primary"
                onClick={() => setIsSubmitConfirmationModalVisible(true)}
                icon={<CheckOutlined />}
                buttonDisabled={isSubmitButtonDisabled}
              >
                Submit
              </AuthZButton>
            </div>
          </div>
        </>,
      );
    } else {
      setPreviewModalFooter(null);
    }
  }, [isPreviewTableLoading, JSON.stringify(previewReviewTasks)]);

  function renderSubmitConfirmationModal() {
    const taskCountForCurrentUser = countOfTasksAddedByCurrentUser();
    const taskCountForOtherAOs = countOfTasksAddedByOtherAccessOwners();
    const totalTasksCount = taskCountForCurrentUser.total + taskCountForOtherAOs.total;
    return (
      <Modal
        className={"submit-confirmation-modal"}
        visible={isSubmitConfirmationModalVisible}
        title={<div style={{ color: "white", fontSize: "18px" }}>Confirmation</div>}
        footer={null}
        closable={false}
        destroyOnClose={true}
        getContainer={popupContainer}
      >
        {renderDecisionsMadeByUsers(taskCountForCurrentUser, "Review decisions you made")}
        {renderDecisionsMadeByUsers(taskCountForOtherAOs, "Review decisions made by other Access Owners")}
        <div style={{ margin: "0.75rem 0" }}>Are you sure, you want to submit all <b>{totalTasksCount}</b> review
          decisions(s)?
        </div>
        <div
          style={{ display: "flex", flexDirection: "row-reverse" }}
          className={"submit-confirmation-modal-content"}
        >
          <AuthZButton authZButtonType={"primary"} onClick={previewModalOkHandler}>
            Submit
          </AuthZButton>
          <AuthZButton
            authZButtonType={"secondary"}
            onClick={() => setIsSubmitConfirmationModalVisible(false)}
          >
            Cancel
          </AuthZButton>
        </div>
      </Modal>
    );
  }

  const renderPreviewTableNote = () => {
    const tasksCount = countOfTasksAddedByOtherAccessOwners();
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <div className="highlight" />
        <div>
          Review decisions made by other Access Owners ({tasksCount.total})
        </div>
      </div>
    );
  };

  const renderShowTotal = (total, range) => {
    if (isSaveAsDraftFeatureEnabled) {
      return (
        <div>
          {renderPreviewTableNote()}
          <div>
            {range[0]}-{range[1]} of {total} items
          </div>
        </div>
      );
    }
    return (
      <div style={{ float: "right" }}>
        {range[0]}-{range[1]} of {total} items
      </div>
    );
  };

  function renderSelectedTasksCountInPreviewTable() {
    if (noOfIgnoredRows === 0) return null;
    return () => <div className="items-selected-count">{noOfIgnoredRows} selected</div>;
  }

  function renderSavedTasksCount() {
    if (isSaveAsDraftFeatureEnabled && reviewTasks.savedForMyTasksCount() > 0) {
      return (
        <div>
          {reviewTasks.savedForMyTasksCount()} out of {reviewTasks.editableTaskCount()} review
          decisions have been saved
        </div>
      );
    }
    return null;
  }

  function renderSaveForLaterNote() {
    if (reviewTasks.shouldShowSubmitNotice() && isSaveAsDraftFeatureEnabled) {
      return (
        <span className="save-for-later-note">
          <span style={{ color: "red", fontWeight: "bold" }}>* </span>
          Not sure about submitting the changes! <b>Save for later</b>
        </span>
      );
    }
    return null;
  }

  useEffect(() => {
    if (!isNullOrUndefined(reviewTasks)) {
      setCurrentReviewTasksInTable(new ReviewTasksModel(reviewTasks.viewableTasks(filters), { pageSize }));
    }
  }, [JSON.stringify(reviewTasks.keys().sort())]);


  function renderExportMyTasksButton() {
    if (showExportMyTasksButton) {
      return (
        <div className="export-my-tasks-button-container">
          <DownloadReport
            reviewId={reviewId}
            appId={appId}
            reviewName={reviewName}
            exportAssignedTasks={true}
            className={"ExportMyTasks"}
          />
        </div>
      );
    }
    return null;
  }

  function renderFullScreenButtons() {
    if (reviewInFullScreenFeatureEnabled) {
      if (fullScreenHandle.active) {
        return (
          <div className="full-screen-button-container">
            <AuthZButton
              icon={<CompressOutlined />}
              onClick={fullScreenHandle.exit}
              authZButtonType={"default"}
              getPopupContainer={popupContainer}
              tooltipTitle={"Exit from Full screen mode"}
              tooltipPlacement={"topLeft"}
            />
          </div>
        );
      }
      return (
        <div className="full-screen-button-container">
          <AuthZButton
            icon={<ExpandOutlined />}
            onClick={fullScreenHandle.enter}
            authZButtonType={"default"}
            getPopupContainer={popupContainer}
            tooltipTitle={"Switch to Full screen mode"}
            tooltipPlacement={"topLeft"}
          />
        </div>
      );
    }

    return null;
  }

  return (
    <FullScreen handle={fullScreenHandle} className={`ReviewTaskContainer-${reviewId}`}>
      <AuthZGuidedTour run={tourState.run} steps={tourState.steps} setTourState={setTourState} />
      {fullScreenHandle.active && appHeader}
      <div>
        <PreviewModalComponent
          visible={isPreviewModalVisible}
          tableProperties={{
            columns: previewAndSubmitColumnGenerator(
              columns,
              previewTasksReasonsAvailableForFilter,
              popupContainer,
            ),
            dataSource: previewTasksDatasource,
            pagination: {
              showSizeChanger: false,
              showTotal: renderShowTotal,
              pageSize: pageSize,
              showQuickJumper: true,
            },
            title: renderSelectedTasksCountInPreviewTable(),
            rowClassName: previewTableRowClassName,
            rowSelection: isSaveAsDraftFeatureEnabled ? rowSelectionAttributes : null,
            loading: isPreviewTableLoading,
            scroll: { x: "max-content", y: "calc(25vh)" },
            getPopupContainer: popupContainer,
          }}
          onOk={previewModalOkHandler}
          okButtonProps={{ onOk: previewModalOkHandler }}
          onCancel={() => setPreviewModalVisible(false)}
          className="ReviewTasksPreviewModal"
          modalHeaderContent={{
            title: "Preview and submit changes",
            content: isSaveAsDraftFeatureEnabled
              ? previewReviewTasks.countOfSubmittableTasks() +
              " review decisions are ready to be submitted"
              : "Are you sure you want to submit the below changes?",
          }}
          modalCustomTitle={isSaveAsDraftFeatureEnabled ? previewModalHeader : null}
          footer={isSaveAsDraftFeatureEnabled ? previewModalFooter : null}
          saveDisabledReason={reviewTasks.saveDisabledReason()}
          destroyOnClose={true}
          getContainer={popupContainer}
        />
        {renderSubmitConfirmationModal()}
        <div className="review-table-actions">
          {renderSavedTasksCount()}
          <div style={{ marginLeft: "auto", padding: "0.8rem 0" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.25rem",
              }}
            >
              <SubmitBoard
                id={`submit-notice-${reviewId}`}
                show={reviewTasks.shouldShowSubmitNotice()}
                submitDisabled={!reviewTasks.canBeSubmitted() || isLoading}
                onSubmitBtnClick={submitButtonHandler}
                saveDisabled={!reviewTasks.canBeSaved() || isLoading}
                onSaveBtnClick={onSaveButtonClick}
                saveDisabledReason={reviewTasks.saveDisabledReason()}
                getPopupContainer={popupContainer}
              />
              {renderExportMyTasksButton()}
              <Popover
                content={
                  <FilterMenu
                    setFilterConfigurations={setFilterConfigurations}
                    filters={filters}
                    showMyListFilter={showMyListFilter}
                    reviewStatus={isReviewInProgress}
                  />
                }
                getPopupContainer={popupContainer}
                getTooltipContainer={popupContainer}
                placement="bottomRight"
                trigger="click"
              >
                <Button
                  icon={<FilterOutlined />}
                  className={`authz-default-button filter-button${
                    isFilterApplied() ? " filter-button-active" : ""
                  }`}
                >
                  Filters {isFilterApplied() ? <>({appliedFiltersCount()})</> : null}
                </Button>
              </Popover>
              {renderFullScreenButtons()}
            </div>
            <div>{renderSaveForLaterNote()}</div>
          </div>
        </div>
        <AuthZTable
          name={"reviewTasks" + reviewId}
          id={reviewId}
          rowSelection={isReviewInProgress && reviewTasks.shouldShowSubmitNotice() && rowSelection}
          rowClassName={rowClassName}
          columns={modifiedColumns}
          dataSource={reviewTasks.viewableTasks(filters)}
          setTabPageSize={setTabPageSize}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: defaultPageSizeOptions,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: defaultPageSize,
            pageSize: pageSize,
            defaultCurrent: 1,
            showQuickJumper: true,
            onShowSizeChange: (current, size) => {
              setTabPageSize({ pageSize: size });
            },
          }}
          getPopupContainer={popupContainer}
          className="time-table-row-select review-tasks-table"
          scroll={{
            x: "fit-content",
            y: fullScreenHandle.active ? "calc(100vh - 310px)" : "calc(100vh - 410px)",
          }}
          loading={isLoading}
          onChange={(pagination, _filters, _sorter, extra) => {
            setCurrentReviewTasksInTable(new ReviewTasksModel(extra.currentDataSource, pagination));
          }}
          onCustomFilterAndCustomSortChange={(_filters, sorter, currentDataSource) => {
            setCurrentReviewTasksInTable(new ReviewTasksModel(currentDataSource, { pageSize }));
            setIsUserGroupingEnabled(
              new ColumnRepresentations(columns).isUserColumn(sorter.columnKey) && !!sorter.order,
            );
          }}
          shouldMakeSelectionColumnLast={true}
          {...titleProps}
        />
      </div>
    </FullScreen>
  );
};
